import { useEffect, useRef, useState } from 'react';
import { PaneControls } from '../../components/Panes';
import { OverflowTabs } from '../../components/OverflowTabs';
import { AudioLines, ChevronLeft, Film, Folder, FolderTree, Globe, Image as ImageIcon, Layers, Plus, Search, Users } from 'lucide-react';
import { formatDuration, frameRateLabel, type GraphicTemplateDTO } from '@prism/contracts';
import { useCollections, useGraphicsSettings, useLibrary, useMe, useTemplateBundle, useTemplates, type CollectionDTO } from '../../api/hooks';
import { graphicsEditor } from '../../graphics/GraphicsEditor';
import { editorDefaultsFor } from '../../graphics/editorDefaults';
import type { EditorFormat } from '../../graphics/editorHost';
import { GraphicPreview } from '../../components/GraphicPreview';
import { flattenTree } from '../../components/AddToCollection';
import { pickableTemplates, setGraphicDrag } from '../../lib/graphic-drag';
import { usePref } from '../../lib/prefs';
import { useLoadMore } from '../library/LibraryItems';
import { RightsMark } from '../../components/RightsMark';

function TemplateCard({ t, onAdd, canEdit }: { t: GraphicTemplateDTO; onAdd: () => void; canEdit: boolean }) {
  // A graphics asset shows its rendered poster; a built-in template runs live (an OGraf graphic has no HTML bundle).
  const bundle = useTemplateBundle(t.asset?.thumbnailUrl || t.engine === 'ograf-v1' ? undefined : t.id, t.version);
  const data = Object.fromEntries(t.manifest.fields.map((f) => [f.id, f.default]));
  return (
    <div className={`bin-template ${canEdit ? 'drag-source' : ''}`} draggable={canEdit} data-testid="bin-template" data-name={t.asset?.title ?? t.manifest.name}
      onDragStart={(e) => setGraphicDrag(e, t.asset ? { assetId: t.asset.id } : { templateId: t.id, version: t.version }, t.asset?.title ?? t.manifest.name)}>
      <div className="bin-template-preview">
        {t.asset?.thumbnailUrl
          ? <img src={t.asset.thumbnailUrl} alt="" style={{ width: '100%', display: 'block' }} />
          : bundle.data && <GraphicPreview bundle={bundle.data} manifest={t.manifest} data={data} t={t.manifest.previewTimeSeconds} durationSeconds={t.manifest.defaultDurationSeconds} />}
      </div>
      <div className="bin-template-body">
        <strong>{t.asset?.title ?? t.manifest.name}</strong>
        <span className="faint" style={{ fontSize: 12 }}>{t.asset ? `${t.engine === 'ograf-v1' ? 'OGraf graphic' : 'Graphics asset'} · v${t.version} · ${t.manifest.name}` : `Built in · v${t.version} · ${t.manifest.description}`}</span>
        {canEdit && <button className="btn sm" onClick={onAdd} data-testid={`add-template-${t.asset ? `asset-${t.asset.title}` : t.id}`}><Plus size={13} /> Add at playhead</button>}
      </div>
    </div>
  );
}

/**
 * New graphic (1.2.0), in the Graphics tab: opens PRISM Graphics on a blank graphic in the sequence's frame size and
 * frame rate. Shown when PRISM Graphics is configured and the person may upload; a save refreshes this list.
 */
function NewGraphicButton({ format }: { format?: EditorFormat }) {
  const settings = useGraphicsSettings();
  const me = useMe().data;
  if (!settings.data?.editorOrigin || !me?.permissions.includes('asset.upload')) return null;
  const defaults = editorDefaultsFor(format);
  return (
    <button type="button" className="btn sm" data-testid="bin-new-graphic"
      title={`Make a graphic in PRISM Graphics, ${defaults.width}×${defaults.height} at ${frameRateLabel(defaults.frameRate)} fps like this sequence`}
      onClick={() => graphicsEditor.open({ mode: 'new', defaults })}>
      <Plus size={13} /> New graphic
    </button>
  );
}

/** The Collections tab's groups (1.7.4; the Media source dropdown before): yours, shared with you, public. */
const SOURCE_GROUPS: Array<{ scope: CollectionDTO['scope']; label: string }> = [
  { scope: 'personal', label: 'My collections' },
  { scope: 'shared', label: 'Shared with me' },
  { scope: 'public', label: 'Public collections' },
];

/** The collection tree of the Collections tab: one row per collection, nested by depth. */
function CollectionTree({ collections, onPick }: { collections: CollectionDTO[]; onPick: (id: string) => void }) {
  const groups = SOURCE_GROUPS.map((g) => ({ ...g, tree: flattenTree(collections.filter((c) => c.scope === g.scope)) })).filter((g) => g.tree.length);
  if (!groups.length) return <p className="muted" style={{ padding: 12 }} data-testid="bin-collections-empty">No collections yet. Make one in Collections to cut from it here.</p>;
  return (
    <div className="bin-collection-tree" data-testid="bin-collections">
      {groups.map((g) => (
        <div key={g.scope} role="group" aria-label={g.label}>
          <p className="section-label">{g.label}</p>
          {g.tree.map((c) => (
            <button key={c.id} type="button" className="bin-collection" style={{ paddingLeft: 8 + c.depth * 14 }} onClick={() => onPick(c.id)}
              data-testid="bin-collection" data-value={c.id} data-label={c.name}>
              {c.scope === 'public' ? <Globe size={13} /> : c.scope === 'shared' ? <Users size={13} /> : <Folder size={13} />}
              <span className="name">{c.name}</span>{c.assets ? <span className="faint count">{c.assets}</span> : null}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/** A list of media, picked by click (into the source viewer), with recordings first and Load more. */
function MediaList({ collection, q, pickedId, onPick, emptyText }: { collection: string | null; q: string; pickedId: string | null; onPick: (assetId: string) => void; emptyText: string }) {
  // Graphics assets are in the Graphics tab (they go on the graphics track).
  const lib = useLibrary({
    q, status: ['ready', 'partial'], kind: ['video', 'audio', 'image'], location: [], category: [], fields: {}, sort: 'newest', includeDismissed: false, collection,
  });
  const loadMore = useLoadMore(lib);
  // Recordings (1.0.0-rc.1) are listed first while they record, with the length recorded so far, and can be cut
  // like any media. They have no media kind until their file is ingested, so they are asked for on their own.
  const recordingsQ = useLibrary({
    q, status: ['recording'], kind: [], location: [], category: [], fields: {}, sort: 'newest', includeDismissed: false, collection,
  }, { refetchInterval: 5000 });
  const recordings = recordingsQ.data?.items ?? [];
  const items = [...recordings, ...(lib.data?.items ?? []).filter((a) => !recordings.some((r) => r.id === a.id))];
  const list = useRef<HTMLDivElement>(null);
  // Reveal in bin (1.7.4): the picked asset scrolls into view.
  useEffect(() => {
    if (!pickedId) return;
    list.current?.querySelector(`[data-asset="${CSS.escape(pickedId)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [pickedId, items.length]);
  return (
    <div className="pane-body bin-list" data-testid="bin-list" ref={list}>
      {items.map((a) => (
        <button key={a.id} className={`bin-item ${pickedId === a.id ? 'selected' : ''}`} onClick={() => onPick(a.id)} onDoubleClick={() => onPick(a.id)} data-testid="bin-item" data-title={a.title} data-asset={a.id}>
          <span className="bin-thumb">{a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" /> : a.mediaKind === 'audio' ? <AudioLines size={16} /> : a.mediaKind === 'image' ? <ImageIcon size={16} /> : <Film size={16} />}</span>
          <span className="bin-meta"><span className="bin-title">{a.title}<RightsMark rights={a.rights} /></span>
            {a.status === 'recording'
              ? <span className="faint mono" data-testid="bin-recording"><span className="bin-rec" title="Recording now: what is recorded so far can be marked and cut">● REC</span>{formatDuration(a.recordedTicks ?? 0)} so far</span>
              : <span className="faint mono">{a.mediaKind === 'image' ? 'Still' : a.durationTicks ? formatDuration(a.durationTicks) : '—'}</span>}
          </span>
        </button>
      ))}
      {lib.data && recordingsQ.data && !items.length && <p className="muted" style={{ padding: 12 }} data-testid="bin-empty">{emptyText}{q ? ' matches' : ''}.</p>}
      {items.length > 0 && (
        <div ref={loadMore} className="load-more" aria-live="polite">
          {lib.isFetchingNextPage ? 'Loading more…' : lib.hasNextPage ? <button className="btn ghost sm" onClick={() => void lib.fetchNextPage()}>Load more</button> : null}
        </div>
      )}
    </div>
  );
}

type BinTab = 'media' | 'collections' | 'graphics';
const binTabOf = (v: unknown): BinTab => (v === 'collections' || v === 'graphics' ? v : 'media');

export function Bin({ onPick, onAddTemplate, pickedId, canEdit, format, reveal }: {
  onPick: (assetId: string) => void;
  onAddTemplate: (t: GraphicTemplateDTO) => void;
  pickedId: string | null;
  canEdit: boolean;
  /** The sequence's frame size and rate, for New graphic (1.2.0). */
  format?: EditorFormat;
  /** Reveal in bin (1.7.4): each new value shows the Media tab with the picked asset in view. */
  reveal?: number;
}) {
  // 1.7.4: Media (all media), Collections (the collection tree, then a collection's media) and Graphics, remembered.
  const [savedTab, setTab] = usePref<string>('editor.bin.tab', 'media');
  const tab = binTabOf(savedTab);
  const [q, setQ] = useState('');
  const [collection, setCollection] = usePref<string | null>('editor.bin.collection', null);
  const collections = useCollections();
  const known = collections.data?.items ?? [];
  const current = collection ? known.find((c) => c.id === collection) ?? null : null;
  // A remembered collection that is gone (deleted, or no longer shared) goes back to the tree.
  useEffect(() => {
    if (collections.data && collection && !collections.data.items.some((c) => c.id === collection)) setCollection(null);
  }, [collections.data, collection, setCollection]);
  const revealed = useRef(reveal);
  useEffect(() => {
    if (reveal === undefined || reveal === revealed.current) return;
    revealed.current = reveal;
    setTab('media');
    setQ('');
  }, [reveal, setTab]);
  const templates = useTemplates();
  const [gq, setGq] = useState('');
  const graphics = pickableTemplates(templates.data?.items ?? []).filter((t) => {
    const text = `${t.asset?.title ?? ''} ${t.asset?.assetCode ?? ''} ${t.manifest.name} ${t.manifest.description}`.toLowerCase();
    return !gq.trim() || text.includes(gq.trim().toLowerCase());
  });
  return (
    <div className="pane raised bin-pane" data-testid="bin">
      <div className="tabs tab-row side-tabs bin-tabs-row" style={{ height: 38 }}>
        <OverflowTabs className="tab-strip" wrapClassName="tab-strip-wrap" role="tablist" label="Bin" activeKey={tab} testId="bin-tabs">
          <button role="tab" className="tab" aria-selected={tab === 'media'} onClick={() => setTab('media')} data-testid="bin-tab-media"><Film size={13} /> Media</button>
          <button role="tab" className="tab" aria-selected={tab === 'collections'} onClick={() => setTab('collections')} data-testid="bin-tab-collections"><FolderTree size={13} /> Collections</button>
          <button role="tab" className="tab" aria-selected={tab === 'graphics'} onClick={() => setTab('graphics')} data-testid="bin-tab-graphics"><Layers size={13} /> Graphics</button>
        </OverflowTabs>
        <PaneControls />
      </div>
      {tab === 'media' && (
        <>
          <div className="bin-toolbar">
            <div className="search">
              <Search size={14} />
              <input className="input" placeholder="Search media…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search media" />
            </div>
          </div>
          <MediaList collection={null} q={q} pickedId={pickedId} onPick={onPick} emptyText="No ready media" />
        </>
      )}
      {tab === 'collections' && (current ? (
        <>
          <div className="bin-toolbar">
            <button type="button" className="btn ghost sm" onClick={() => setCollection(null)} data-testid="bin-collection-back" aria-label="All collections" title="Back to all collections"><ChevronLeft size={13} /></button>
            <span className="bin-collection-current" data-testid="bin-collection-current" title={current.name}>{current.scope === 'public' ? <Globe size={13} /> : <Folder size={13} />} {current.name}</span>
            <div className="search">
              <Search size={14} />
              <input className="input" placeholder="Search this collection…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search this collection" />
            </div>
          </div>
          <MediaList collection={current.id} q={q} pickedId={pickedId} onPick={onPick} emptyText="No ready media in this collection" />
        </>
      ) : (
        <div className="pane-body">
          {collections.data && <CollectionTree collections={known} onPick={(id) => { setCollection(id); setQ(''); }} />}
        </div>
      ))}
      {tab === 'graphics' && (
        <>
          <div className="bin-toolbar bin-graphics-toolbar">
            <div className="search">
              <Search size={14} />
              <input className="input" placeholder="Search graphics…" value={gq} onChange={(e) => setGq(e.target.value)} aria-label="Search graphics" data-testid="search-graphics" />
            </div>
            <NewGraphicButton format={format} />
          </div>
          <div className="pane-body" style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="bin-graphics">
            {graphics.map((t) => <TemplateCard key={`${t.id}@${t.version}`} t={t} canEdit={canEdit} onAdd={() => onAddTemplate(t)} />)}
            {templates.data && !graphics.length && <p className="muted" style={{ padding: 4 }}>{gq ? 'No graphics match.' : 'No graphics yet. Upload a graphics package (.zip) to add your own.'}</p>}
            {canEdit && graphics.length > 0 && <p className="faint" style={{ fontSize: 12, margin: 0 }}>Drag a graphic onto the graphics track, or add it at the playhead.</p>}
          </div>
        </>
      )}
    </div>
  );
}
